import {
    type ViewStyle,
    type TextStyle,
    StyleSheet
} from "react-native";
import {
    type NumericInputDynamicStyleType,
    type NumericInputTypes,
    type NumericInputType
} from "./type";
import type {
    Mutable
} from "../../types";

export const NUMERIC_INPUT_TYPE_STYLES: Record<
    NumericInputType,
    NumericInputTypes
> = {
    default: {
        focusBorderColor: "emphasized",
        borderColor: "emphasized",
        hintTextIconColor: "mid",
        placeholderColor: "low",
        containerColor: "mid",
        hintTextColor: "mid",
        titleColor: "high",
        inputColor: "mid",
        iconColor: "mid"
    },
    question: {
        focusBorderColor: "emphasized",
        borderColor: "emphasized",
        hintTextIconColor: "mid",
        placeholderColor: "low",
        containerColor: "mid",
        hintTextColor: "mid",
        titleColor: "mid",
        inputColor: "mid",
        iconColor: "mid"
    },
    success: {
        placeholderColor: "successLow",
        hintTextIconColor: "success",
        focusBorderColor: "success",
        containerColor: "success",
        hintTextColor: "success",
        borderColor: "success",
        titleColor: "success",
        inputColor: "success",
        iconColor: "success"
    },
    warning: {
        placeholderColor: "warningLow",
        hintTextIconColor: "warning",
        focusBorderColor: "warning",
        containerColor: "warning",
        hintTextColor: "warning",
        borderColor: "warning",
        titleColor: "warning",
        inputColor: "warning",
        iconColor: "warning"
    },
    danger: {
        placeholderColor: "dangerLow",
        hintTextIconColor: "danger",
        focusBorderColor: "danger",
        containerColor: "danger",
        hintTextColor: "danger",
        borderColor: "danger",
        titleColor: "danger",
        inputColor: "danger",
        iconColor: "danger"
    },
    info: {
        placeholderColor: "infoLow",
        hintTextIconColor: "info",
        focusBorderColor: "info",
        containerColor: "info",
        hintTextColor: "info",
        borderColor: "info",
        titleColor: "info",
        inputColor: "info",
        iconColor: "info"
    }
};

export const getNumericInputType = ({
    type
}: {
    type: NumericInputType;
}) => {
    const currentType = NUMERIC_INPUT_TYPE_STYLES[type];

    return currentType;
};

const stylesheet = StyleSheet.create({
    input: {
        backgroundColor: "transparent",
        textAlignVertical: "center",
        borderColor: "transparent",
        paddingBottom: 0,
        paddingRight: 0,
        paddingLeft: 0,
        borderWidth: 0,
        paddingTop: 0,
        minHeight: 20,
        zIndex: 99,
        flex: 1
    },
    cleanButton: {
        justifyContent: "center",
        alignItems: "center",
        alignSelf: "center",
        display: "flex",
        zIndex: 99
    },
    hideTextIconContainer: {
        justifyContent: "center",
        alignItems: "center",
        alignSelf: "center",
        display: "flex",
        zIndex: 99,
        height: 18
    },
    icon: {
        justifyContent: "center",
        alignContent: "center",
        alignItems: "center",
        display: "flex",
        zIndex: 99
    },
    rightIcon: {
        justifyContent: "center",
        alignItems: "center",
        alignSelf: "center",
        display: "flex",
        zIndex: 99
    },
    container: {
        flexDirection: "column",
        boxSizing: "border-box",
        display: "flex"
    },
    content: {
        boxSizing: "border-box",
        flexDirection: "row",
        borderStyle: "solid",
        alignItems: "center",
        position: "relative",
        display: "flex"
    },
    titleContainer: {
        flexDirection: "row",
        alignItems: "center"
    },
    hintText: {
        flexDirection: "row",
        alignItems: "center",
        display: "flex"
    },
    overlay: {
        position: "absolute",
        zIndex: 98,
        bottom: 0,
        right: 0,
        left: 0,
        top: 0
    },
    hintTextIcon: {
    },
    required: {
    },
    subTitle: {
    },
    title: {
    }
});

export const useStyles = ({
    spreadBehaviour,
    inlineSpaces,
    currentType,
    isDisabled,
    typography,
    isFocused,
    radiuses,
    borders,
    colors,
    spaces,
    type
}: NumericInputDynamicStyleType) => {
    const styleType = type === "danger" ? "error" : type === "question" ? "neutral" : type;

    const styles = {
        content: {
            minWidth: (typography.labelLargeSize.fontSize * 2) + (spaces.spacingMd * 2),
            backgroundColor: colors.content.container[currentType.containerColor],
            borderColor: colors.content.container[currentType.containerColor],
            paddingBottom: spaces.spacingMd,
            paddingRight: spaces.spacingMd,
            borderRadius: radiuses.actions,
            paddingLeft: spaces.spacingMd,
            paddingTop: spaces.spacingMd,
            borderWidth: borders.line
        } as Mutable<ViewStyle>,
        input: {
            ...typography.labelLargeSize,
            color: colors.content.text[currentType.inputColor]
        } as Mutable<TextStyle>,
        required: {
            marginRight: inlineSpaces.required
        } as Mutable<ViewStyle>,
        overlay: {
            borderRadius: radiuses.actions - 1
        } as Mutable<ViewStyle>,
        subTitle: {
            marginLeft: inlineSpaces.subTitle
        } as Mutable<TextStyle>,
        titleContainer: {
            marginBottom: spaces.spacingSm
        } as Mutable<ViewStyle>,
        icon: {
            marginRight: spaces.spacingSm
        } as Mutable<ViewStyle>,
        hintTextIcon: {
            marginRight: spaces.spacingXs
        } as Mutable<ViewStyle>,
        cleanButton: {
            marginLeft: spaces.spacingSm
        } as Mutable<ViewStyle>,
        hideTextIconContainer: {
            marginLeft: spaces.spacingSm
        } as Mutable<ViewStyle>,
        rightIcon: {
            marginLeft: spaces.spacingSm
        } as Mutable<ViewStyle>,
        hintText: {
            marginTop: spaces.spacingSm
        } as Mutable<TextStyle>,
        container: {
            alignSelf: "auto"
        } as Mutable<ViewStyle>,
        title: {
        } as Mutable<TextStyle>
    };

    if(isDisabled) {
        const disableStyleType = styleType === "default" ? "neutral" : styleType;

        styles.overlay.backgroundColor = colors.system.state.overlay.disabled[disableStyleType];
        styles.container.borderColor = colors.system.state.overlay.disabled[disableStyleType];

        styles.input.color = colors.content.text.disabled;
    }

    if (spreadBehaviour === "baseline") {
        styles.container.alignSelf = spreadBehaviour;
        styles.container.width = "auto";
    }

    if (spreadBehaviour === "stretch") {
        styles.container.alignSelf = spreadBehaviour;
        styles.container.justifyContent = "center";
        styles.container.flexShrink = 1;
        styles.container.width = "100%";
    }

    if(isFocused && !isDisabled) {
        if(type === "question" || type === "default") {
            styles.content.borderColor = colors.content.border.emphasized;
        } else {
            styles.content.borderColor = colors.content.border[type];
        }
    }

    return styles;
};
export default stylesheet;
